/* @flow */

import DMSConsumer from 'CoreConsumers/DMSConsumer';
import { RequestStatus } from 'CoreConstants';
import type { $AxiosXHR } from 'axios';
import type { $Coordinates } from 'CoreComponents/MapProviders/Coordinates';
import type { $Dealer } from 'CoreTypes/Dealer';
import type { $RequestStatus } from 'CoreTypes/RequestStatus';
import type { $SearchRequest } from 'CoreTypes/SearchRequest';
import type { $SearchResults } from 'CoreTypes/SearchResults';
import type { $Suggestion } from 'CoreTypes/AutoCompleteSuggestions';
import type { $WidgetState } from '../types/WidgetState';
import type { $WidgetEnvConfiguration } from '../types/WidgetEnvConfiguration';
import { getConfigurationMarketProperty } from 'CoreUtils/WidgetUtils';
import { getDMSEndpointURL } from 'CoreUtils/EndpointUtils';
import { getEnvConfiguration } from '../environments';
import { setDealersSearchFilteringCriteria } from 'CoreStore/filters';
import { TemplateMarketPropertyKeys } from 'CoreTemplates/TemplateMarketProperties';
import Globals from 'CoreConstants/Globals';
import { DefaultDistanceOptions } from 'CoreConstants/DistanceUnits';
import ViewMode from '../components/SearchResults/SearchResultsViewMode';

export const FIND_DEALERS_REQUEST = 'dsw/search/FIND_DEALERS_REQUEST';
export const FIND_DEALERS_SUCCESS = 'dsw/search/FIND_DEALERS_SUCCESS';
export const FIND_DEALERS_FAILURE = 'dsw/search/FIND_DEALERS_FAILURE';
export const FIND_DEALERS_LIST_PAGE_REQUEST = 'dsw/search/FIND_DEALERS_LIST_PAGE_REQUEST';
export const FIND_DEALERS_LIST_PAGE_SUCCESS = 'dsw/search/FIND_DEALERS_LIST_PAGE_SUCCESS';
export const FIND_DEALERS_LIST_PAGE_FAILURE = 'dsw/search/FIND_DEALERS_LIST_PAGE_FAILURE';
export const SET_DEALERS_LIST_PAGE = 'dsw/search/SET_DEALERS_LIST_PAGE';
export const INCREASE_SEARCH_COUNT = 'dsw/search/INCREASE_SEARCH_COUNT';

export type $State = {
  resultsMap: Array<$Dealer>,
  resultsList: Array<Array<$Dealer>>,
  resultsListPage: number,
  resultsListPagePrevRequest: number,
  requestStatus: $RequestStatus,
  requestPageStatus: $RequestStatus,
  totalResults: ?number,
  coordinates: ?$Coordinates,
  searchTerm: string,
  suggestedSearch: ?string,
  lastRequestParams: ?$FindDealersOptions,
  searchCount: number
};

type $GetStateFunction = () => { dsw: $WidgetState };

type $RequestDealersOptions = {
  state: $WidgetState,
  isSearchByLocationActive?: boolean,
  searchLocationString: ?string,
  searchNameString: ?string,
  spellCheck?: boolean,
  forceGeolocation?: boolean,
  dealerFields?: Array<string>,
  page?: number,
  sortProperty?: string,
  size?: number,
  suggestion?: $Suggestion
};

type $RequestMapDealersOptions = {
  state: $WidgetState,
  isSearchByLocationActive?: boolean,
  searchLocationString: ?string,
  searchNameString: ?string,
  spellCheck?: boolean,
  forceGeolocation?: boolean,
  suggestion?: $Suggestion
};

type $RequestListDealersOptions = $RequestMapDealersOptions & {
  page: number
};

export type $FindDealersOptions = {
  isSearchByLocationActive?: boolean,
  searchLocationString: ?string,
  searchNameString: ?string,
  spellCheck?: boolean,
  forceGeolocation?: boolean,
  suggestion?: $Suggestion
};

export type $FindDealersListPageOptions = {
  page: number
};

const initialState: $State = {
  resultsMap: [],
  resultsList: [],
  resultsListPage: 1,
  resultsListPagePrevRequest: 1,
  requestStatus: RequestStatus.INIT,
  requestPageStatus: RequestStatus.INIT,
  totalResults: null,
  coordinates: null,
  searchTerm: '',
  suggestedSearch: null,
  suggestion: null,
  lastRequestParams: null,
  searchCount: 0
};

function requestDealers({
  state,
  isSearchByLocationActive,
  searchLocationString,
  searchNameString,
  spellCheck,
  forceGeolocation,
  suggestion,
  dealerFields,
  page,
  size,
  sortProperty
}: $RequestDealersOptions): Promise<$AxiosXHR<$SearchResults>> {
  if (!state.configuration)
    throw new Error('Configuration not yet defined in dsw state');

  const { env, market, whiteList: settingsWhiteList, blackList: settingsBlackList } = state.settings;
  /**
   * We always have 1 language otherwise we don't render
   */
  const { configuration } = state;
  const languages = state.configuration.languages;
  const localeLanguage = !!(languages && languages[0] && languages[0].localeLanguage);
  const ignoreStrictGeoConfig = getConfigurationMarketProperty(
    configuration || {},
    market,
    TemplateMarketPropertyKeys.GEN_IGNORE_STRICT_GEO
  );

  const requestParams: $SearchRequest = {
    strictGeo: !ignoreStrictGeoConfig,
    marketCode: market,
    localeLanguage
  };

  /* Configuration external ID */
  if (configuration && configuration.baseInfo && configuration.baseInfo.externalId)
    requestParams.configurationExternalId = configuration.baseInfo.externalId;

  /* Search profile name */
  const searchProfileName = state.settings.searchProfileName;
  if (searchProfileName) requestParams.searchProfileName = searchProfileName;

  /* Maximum number of search results */
  requestParams.size = size || Globals.DEFAULT_DEALERS_SEARCH_COUNT;

  /* Search string */
  if(suggestion) {
    requestParams.locationId = suggestion.locationId;
  } else {
    if (isSearchByLocationActive && searchLocationString) requestParams.location = searchLocationString;
    if (!isSearchByLocationActive && searchNameString) requestParams.name = searchNameString;
  }

  /* Force original search flag */
  if (spellCheck) requestParams.spellCheck = true;

  /* Geolocation */
  if (forceGeolocation && state && state.geolocation && state.geolocation.position &&
    state.geolocation.position.latitude && state.geolocation.position.longitude &&
    !searchLocationString && !searchNameString) {
    requestParams.coordinates = {
      latitude: state.geolocation.position.latitude,
      longitude: state.geolocation.position.longitude
    };
  }

  /* Search radius and measurement unit */
  const defaultSearchRadius = DefaultDistanceOptions.RADIUS_25;

  const marketSearchRadius = getConfigurationMarketProperty(
    configuration, market, TemplateMarketPropertyKeys.GEN_SEARCH_RADIUS
  );

  const marketRadiusOptions: Array<?string> = [
    getConfigurationMarketProperty(
      configuration || {},
      market,
      TemplateMarketPropertyKeys.GEN_SEARCH_RADIUS_DISTANCE_MIN) || null,
    getConfigurationMarketProperty(
      configuration || {},
      market,
      TemplateMarketPropertyKeys.GEN_SEARCH_RADIUS_DISTANCE_MID) || null,
    getConfigurationMarketProperty(
      configuration || {},
      market,
      TemplateMarketPropertyKeys.GEN_SEARCH_RADIUS_DISTANCE_MAX) || null,
  ];

  const distanceUnit = getConfigurationMarketProperty(
    configuration, market, TemplateMarketPropertyKeys.GEN_DISTANCE_UNIT
  ) || Globals.DEFAULT_DISTANCE_UNIT;

  const distanceFilter: any = state && state.filters && state.filters.searchRadius;

  if (distanceUnit) {
    // If user selected a radius filter
    if (distanceFilter && distanceFilter.length === 1)
      requestParams.distance = `${distanceFilter[0]}${distanceUnit}`;
    // If there is a market search radius set
    else if (marketSearchRadius)
      requestParams.distance = `${marketSearchRadius}${distanceUnit}`;
    // If all the options for distance filters are provided
    else if (marketRadiusOptions.every(marketRadiusOption => marketRadiusOption !== null))
      requestParams.distance = `${marketRadiusOptions[1] || ''}${distanceUnit}`;
    else
      requestParams.distance = `${defaultSearchRadius}${distanceUnit}`;
  }

  /* Extended / wide search radius search flag */
  const extendedSearchRadius = getConfigurationMarketProperty(
    configuration, market, TemplateMarketPropertyKeys.GEN_SEARCH_RADIUS_EXTENDED
  );

  if (extendedSearchRadius === true) requestParams.expand = true;

  /* Filters */
  const filteringCriteria = setDealersSearchFilteringCriteria(state.filters || {}, state.configuration || {}, market);
  if (filteringCriteria) requestParams.filters = filteringCriteria;

  /* Dealer fields */
  if (dealerFields) requestParams.fields = dealerFields.join(',');

  /* Request page */
  if (page) requestParams.page = page;

  /* Request sort property */
  if (sortProperty) requestParams.sortProperty = sortProperty;

  /* Settings or configuration market properties whitelist */
  const marketConfigWhiteList = getConfigurationMarketProperty(
    state.configuration || {},
    state.settings.market,
    TemplateMarketPropertyKeys.GEN_WHITELIST
  );
  if (settingsWhiteList) {
    requestParams.whiteList = Array.isArray(settingsWhiteList)
      ? settingsWhiteList.join(',')
      : settingsWhiteList;
  } else if (marketConfigWhiteList) {
    requestParams.whiteList = Array.isArray(marketConfigWhiteList)
      ? marketConfigWhiteList.join(',')
      : marketConfigWhiteList;
  }

  /* Settings or configuration market properties blacklist */
  const marketConfigBlackList = getConfigurationMarketProperty(
    state.configuration || {},
    state.settings.market,
    TemplateMarketPropertyKeys.GEN_BLACKLIST
  );
  if (settingsBlackList) {
    requestParams.blackList = Array.isArray(settingsBlackList)
      ? settingsBlackList.join(',')
      : settingsBlackList;
  } else if (marketConfigBlackList) {
    requestParams.blackList = Array.isArray(marketConfigBlackList)
      ? marketConfigBlackList.join(',')
      : marketConfigBlackList;
  }

  const envConfig: $WidgetEnvConfiguration = getEnvConfiguration(env);
  const dms = new DMSConsumer(getDMSEndpointURL(state.settings, envConfig));

  return dms.searchDealersFullText(requestParams);
}

function requestDealersMap({
  state,
  isSearchByLocationActive,
  searchLocationString,
  searchNameString,
  spellCheck,
  forceGeolocation,
  suggestion
}: $RequestMapDealersOptions): Promise<$AxiosXHR<$SearchResults>> {
  if (!state.configuration)
    throw new Error('Configuration not yet defined in dsw state');

  /* DL-1439 */
  const searchResultsLimit = Globals.DEFAULT_DEALERS_SEARCH_COUNT;

  return requestDealers({
    state,
    isSearchByLocationActive,
    searchLocationString,
    searchNameString,
    spellCheck,
    forceGeolocation,
    size: searchResultsLimit,
    suggestion
  });
}

function requestDealersList({
  state,
  isSearchByLocationActive,
  searchLocationString,
  searchNameString,
  spellCheck,
  forceGeolocation,
  page,
  suggestion
}: $RequestListDealersOptions): Promise<$AxiosXHR<$SearchResults>> {
  if (!state.configuration)
    throw new Error('Configuration not yet defined in dsw state');

  const size = Number(getConfigurationMarketProperty(
    state.configuration || {},
    state.settings.market,
    TemplateMarketPropertyKeys.DSW_SEARCH_RESULTS_PAGINATION_PAGE_SIZE
  )) || Globals.DEFAULT_DEALERS_PAGINATION_PAGE_SIZE;

  return requestDealers({
    state,
    isSearchByLocationActive,
    searchLocationString,
    searchNameString,
    spellCheck,
    forceGeolocation,
    page,
    size,
    sortProperty: Globals.DEFAULT_SORT_PROPERTY,
    dealerFields: [
      'baseInfo',
      'contact.phone',
      'brands',
      'address',
      'functions.activityCode'
    ],
    suggestion
  });
}

function createPageStruct(
  totalResults: number, pageSize: number, firstPageResults: Array<$Dealer>
): Array<Array<$Dealer>> {
  const pageCount = Math.ceil(totalResults / pageSize);
  const pages = new Array(pageCount).fill([]);
  if (pageCount > 0) pages[0] = firstPageResults;

  return pages;
}

function updatePageStruct(
  pageStruct: Array<Array<$Dealer>>,
  page: number,
  pageResults: Array<$Dealer>
): Array<Array<$Dealer>> {
  const index = page - 1;
  return [
    ...pageStruct.slice(0, index),
    pageResults,
    ...pageStruct.slice(index + 1)
  ];
}

export function findDealersRequest() {
  return { type: FIND_DEALERS_REQUEST };
}

export function findDealersSuccess(
  resultsMap: Array<$Dealer>,
  resultsList: Array<$Dealer>,
  totalResults: number,
  pageSize: number,
  coordinates: ?{latitude: number, longitude: number},
  searchTerm: string,
  suggestedSearch: ?string,
  lastRequestParams: ?$FindDealersOptions,
  suggestion?: $Suggestion
) {
  let payload: Object = {
    type: FIND_DEALERS_SUCCESS,
    resultsMap,
    resultsList: createPageStruct(totalResults, pageSize, resultsList),
    totalResults,
    searchTerm,
    suggestedSearch,
    lastRequestParams,
    suggestion
  };
  if (coordinates && coordinates.latitude && coordinates.longitude) {
    payload.coordinates = {
      lat: coordinates.latitude,
      lng: coordinates.longitude
    }
  }
  return payload;
}

export function findDealersFailure() {
  return { type: FIND_DEALERS_FAILURE };
}

export function findDealers(requestOptions: $FindDealersOptions) {
  return async function(dispatch: Function, getState: $GetStateFunction): Promise<?Array<Array<$Dealer>>> {
    dispatch(findDealersRequest());

    try {
      const state = getState().dsw;
      const initialViewMode = state.viewMode.initial;

      let responseMap: $AxiosXHR<$SearchResults>;
      let responseList: $AxiosXHR<$SearchResults>;

      if (initialViewMode === ViewMode.MAP_MODE.key) {
        responseMap = await requestDealersMap({ ...requestOptions, state });
      }
      else if (initialViewMode === ViewMode.LIST_MODE.key) {
        responseList = await requestDealersList({... requestOptions, state, page: 1 });
      }
      else {
        [
          responseMap,
          responseList
        ] = await Promise.all([
          requestDealersMap({ ...requestOptions, state }),
          requestDealersList({... requestOptions, state, page: 1 })
        ]);
      }

      const pageSize = Number(getConfigurationMarketProperty(
        state.configuration || {},
        state.settings.market,
        TemplateMarketPropertyKeys.DSW_SEARCH_RESULTS_PAGINATION_PAGE_SIZE
      )) || Globals.DEFAULT_DEALERS_PAGINATION_PAGE_SIZE;

      /* DL-1436 */
      const searchValue = requestOptions.isSearchByLocationActive ?
        requestOptions.searchLocationString : requestOptions.searchNameString;

      dispatch(findDealersSuccess(
        (responseMap && responseMap.data.results) || [],
        (responseList && responseList.data.results) || [],
        (responseMap && responseMap.data.total) || (responseList && responseList.data.total) || 0, /* DL-1437 */
        pageSize,
        (responseMap && responseMap.data.coordinates) || (responseList && responseList.data.coordinates) || null,
        searchValue || '',
        (responseMap && responseMap.data.searchTerm) || (responseList && responseList.data.searchTerm) || null,
        requestOptions,
        requestOptions.suggestion
      ));

      dispatch(increaseSearchCount());

      return [
        (responseMap && responseMap.data.results) || [],
        (responseList && responseList.data.results) || []
      ];
    }
    catch (error) {
      dispatch(findDealersFailure());
    }
  }
}

export function findDealersListPageRequest() {
  return { type: FIND_DEALERS_LIST_PAGE_REQUEST };
}

export function findDealersListPageSuccess(page: number, pageResults: Array<$Dealer>) {
  return {
    type: FIND_DEALERS_LIST_PAGE_SUCCESS,
    page,
    resultsListPagePrevRequest: page,
    pageResults
  };
}

export function findDealersListPageFailure() {
  return { type: FIND_DEALERS_LIST_PAGE_FAILURE };
}

export function findDealersListPage(page: number) {
  return async function(dispatch: Function, getState: $GetStateFunction): Promise<?Array<$Dealer>> {
    dispatch(findDealersListPageRequest());

    try {
      const state = getState().dsw;
      const { lastRequestParams } = state.search;
      if (!lastRequestParams) throw new Error('No base request was performed before asking for a page');

      const response = await requestDealersList({ ...lastRequestParams, state, page });
      dispatch(findDealersListPageSuccess(page, response.data.results));
      dispatch(increaseSearchCount());

      return response.data.results;
    }
    catch (error) {
      dispatch(findDealersListPageFailure());
    }
  }
}

export const increaseSearchCount = () => ({
  type: INCREASE_SEARCH_COUNT
});

export const setDealersListPage = (resultsListPage: number) => ({
  type: SET_DEALERS_LIST_PAGE,
  resultsListPage
});

export default function reducer(state: $State = initialState, action: Object): $State {
  switch (action.type) {

    case FIND_DEALERS_REQUEST:
      return {
        ...state,
        requestStatus: RequestStatus.PENDING,
        resultsMap: [],
        resultsList: [],
        totalResults: null,
        searchTerm: '',
        suggestedSearch: null,
        lastRequestParams: null,
        suggestion: null
      };

    case FIND_DEALERS_SUCCESS:
      return {
        ...state,
        resultsMap: action.resultsMap,
        resultsList: action.resultsList,
        requestStatus: RequestStatus.SUCCESS,
        totalResults: action.totalResults,
        coordinates: action.coordinates || null,
        searchTerm: action.searchTerm,
        suggestedSearch: action.suggestedSearch,
        lastRequestParams: action.lastRequestParams,
        suggestion: action.suggestion
      };

    case FIND_DEALERS_FAILURE:
      return {
        ...state,
        resultsMap: [],
        resultsList: [],
        requestStatus: RequestStatus.FAILURE,
        totalResults: null,
        searchTerm: '',
        suggestedSearch: null,
        coordinates: null,
        lastRequestParams: null,
        suggestion: null
      };

    case FIND_DEALERS_LIST_PAGE_REQUEST:
      return {
        ...state,
        requestPageStatus: RequestStatus.PENDING
      };

    case FIND_DEALERS_LIST_PAGE_SUCCESS:
      return {
        ...state,
        requestPageStatus: RequestStatus.SUCCESS,
        resultsList: updatePageStruct(state.resultsList, action.page, action.pageResults),
        resultsListPagePrevRequest: action.resultsListPagePrevRequest
      };

    case FIND_DEALERS_LIST_PAGE_FAILURE:
      return {
        ...state,
        requestPageStatus: RequestStatus.FAILURE
      };

    case INCREASE_SEARCH_COUNT:
      return {
        ...state,
        searchCount: ++state.searchCount
      };

    case SET_DEALERS_LIST_PAGE:
      return {
        ...state,
        resultsListPage: action.resultsListPage
      };

    default:
      return state;

  }
}
